import { Link, useLocation, type LinkProps } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: RouteKey | string
}

export function LocalizedLink({ to, children, onClick, ...props }: LocalizedLinkProps) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const currentLang = i18n.language as SupportedLanguage

  let path: string

  if (to in localizedRoutes) {
    const routeKey = to as RouteKey
    const localizedPath = localizedRoutes[routeKey][currentLang]
    path = localizedPath ? `/${currentLang}/${localizedPath}` : `/${currentLang}`
  } else if (to.startsWith('/')) {
    // Absolute path - add language prefix
    path = `/${currentLang}${to}`
  } else if (to.startsWith('http') || to.startsWith('mailto:') || to.startsWith('tel:')) {
    // External link - pass through
    path = to
  } else {
    // Relative path - add language prefix
    path = `/${currentLang}/${to}`
  }

  const normalizedCurrent = location.pathname.replace(/\/$/, '') || '/'
  const normalizedPath = path.replace(/\/$/, '') || '/'
  const isCurrentPage = normalizedCurrent === normalizedPath

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isCurrentPage) {
      e.preventDefault()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    onClick?.(e)
  }

  return (
    <Link to={path} onClick={handleClick} {...props}>
      {children}
    </Link>
  )
}
